import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import Navbar from "../components/Navbar";
import { ShieldCheck, FileText, Search, AlertTriangle, CheckCircle2, Award } from "lucide-react";
import { API } from "../lib/api";
import { toast } from "sonner";

/**
 * Public Rx-verification page.
 *
 * Pharmacists, family members, or second-line doctors paste the 8-char Rx ID
 * from the PDF footer to confirm the prescription is genuine.
 *
 * NO authentication required — we return only sanitised metadata (doctor,
 * MCI/NMC reg, date, medication count) — never the actual medication list.
 */
export default function VerifyRx() {
  const { id: paramId } = useParams();
  const nav = useNavigate();
  const [rxId, setRxId] = useState(paramId || "");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const lookup = async (idArg) => {
    const id = (idArg ?? rxId).trim();
    if (id.length < 6) { setErr("Rx ID must be at least 6 characters"); return; }
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await fetch(`${API}/verify-rx/${encodeURIComponent(id)}`);
      if (r.status === 400) { setErr("Invalid Rx ID format"); return; }
      const data = await r.json();
      setResult(data);
      if (data?.verified === false) setErr("No prescription found with that ID. Please double-check the ID printed on the PDF.");
    } catch (e) {
      setErr("Could not reach the verifier. Try again in a moment.");
    } finally { setBusy(false); }
  };

  useEffect(() => { if (paramId) lookup(paramId); }, [paramId]); // eslint-disable-line

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-semibold uppercase tracking-wider border border-blue-200">
            <ShieldCheck className="w-3.5 h-3.5"/> Verify a NeuroOpinion prescription
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-3">
            Is this prescription real?
          </h1>
          <p className="text-slate-600 mt-2 max-w-xl mx-auto">
            Paste the 8-character <span className="font-mono font-semibold">Rx ID</span> printed on the
            prescription PDF (bottom-right corner). We&apos;ll confirm it was issued by a NeuroOpinion-verified
            specialist without revealing the medications.
          </p>
        </div>

        <Card className="p-5 border-slate-200">
          <Label htmlFor="rx-input" className="text-slate-700">Rx ID</Label>
          <div className="mt-1 flex gap-2">
            <Input
              id="rx-input"
              value={rxId}
              onChange={(e) => setRxId(e.target.value.replace(/[^a-zA-Z0-9-]/g, ""))}
              placeholder="e.g., 7A3B12F9"
              className="font-mono uppercase tracking-wider"
              onKeyDown={(e) => e.key === "Enter" && lookup()}
              data-testid="verify-rx-input"
              autoFocus
            />
            <Button onClick={() => lookup()} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="verify-rx-btn">
              <Search className="w-4 h-4 mr-2"/> {busy ? "Checking…" : "Verify"}
            </Button>
          </div>
          {err && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800" data-testid="verify-rx-error">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/> {err}
            </div>
          )}
        </Card>

        {result?.verified === true && (
          <Card className="mt-6 p-6 border-emerald-200 bg-emerald-50/50" data-testid="verify-rx-result">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-emerald-100 border border-emerald-300 grid place-items-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-700"/>
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700">Authentic</div>
                <div className="text-lg font-heading font-semibold text-slate-900">Prescription verified</div>
                <div className="text-xs text-slate-500">Issued by {result.issuer} · Rx ID {result.rx_id_short}</div>
              </div>
            </div>

            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              <Row label="Doctor" value={result.doctor.name} highlight={result.doctor.is_featured ? "Founder" : null}/>
              <Row label="Specialisation" value={result.doctor.specialization || "—"}/>
              <Row label="MCI / NMC reg." value={result.doctor.registration_number || "—"} mono/>
              <Row label="Alma mater" value={result.doctor.alma_mater || "—"}/>
              <Row label="Patient (initials)" value={result.patient_initials} mono/>
              <Row label="Mode" value={(result.mode || "").toString().toUpperCase()}/>
              <Row label="Version" value={`v${result.version}`}/>
              <Row label="Medications on file" value={`${result.medication_count} item${result.medication_count !== 1 ? "s" : ""}`}/>
              <Row label="Patient consent" value={result.consent_recorded ? "Recorded" : "Not recorded"}/>
              <Row
                label="Issued at"
                value={result.issued_at ? new Date(result.issued_at).toLocaleString() : "—"}
              />
            </div>

            {result.schedule_x_flagged && (
              <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
                <span>
                  <strong>Schedule-X drug present</strong> — Indian law requires a physical, doctor-signed prescription
                  to dispense Schedule-X medication. The patient should carry the printed PDF + ID proof.
                </span>
              </div>
            )}

            {result.diagnosis_preview && (
              <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Diagnosis (preview)</div>
                <div className="text-sm text-slate-800 mt-1">{result.diagnosis_preview}{result.diagnosis_preview.length >= 120 ? "…" : ""}</div>
              </div>
            )}

            <p className="mt-4 text-[11px] text-slate-500">
              For privacy, the actual medication list is never shown on this page. Ask the patient to share the
              full PDF if you need the prescription details.
            </p>
          </Card>
        )}

        <div className="mt-8 text-center text-sm text-slate-600">
          <Link to="/" className="text-blue-700 hover:underline">← Back to home</Link>
          <span className="mx-3 text-slate-300">·</span>
          <button
            onClick={() => nav("/contact")}
            className="text-blue-700 hover:underline"
            data-testid="verify-rx-contact-link"
          >
            Report a suspected fake prescription
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, highlight, mono }) {
  return (
    <div className="rounded-lg border border-emerald-200 bg-white p-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className={`mt-1 text-slate-900 ${mono ? "font-mono text-sm" : "text-sm font-medium"}`}>
        {value}
        {highlight && (
          <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold border border-amber-200">
            <Award className="w-3 h-3"/> {highlight}
          </span>
        )}
      </div>
    </div>
  );
}
